import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection/WebViewDetails.js";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import CancelablePromise from "cancelable-promise";
import { ParsedFormat, SizePreferencePolicy, getParsedFormats } from "../FormatAndSizePreference.js";
import { VideoResource } from "../../MediaResource.js";
import { VideoPlayerControls, VideoPlayerElements } from "../../Player.js";
import { CastablePlayerTechFactoryWithSizePreferencePolicy } from "../VideoPlayerTechFactory.js";
import { NativeVODPlayer } from "./NativeVODPlayer.js";
import { NativeVODPlayerMediaResource } from "./NativeVODPlayerMediaResource.js";
import { VideoInfoRawData } from "../../../../model/video/VideoInfo.js";
import { NativeVODLoadStrategy, NativeVODPlayerFactoryConfig } from "./NativeVODPlayerFactory.js";

/**
 * Castable variant of {@link NativeVODPlayerFactory}. Produces {@link NativeVODPlayer}
 * instances while exposing Chromecast / AirPlay support through {@link CastablePlayerTechFactory}.
 *
 * Intended to be used in flows where there are no ads, so that switching playback to a
 * remote receiver does not need to be coordinated with an ad controller.
 */
export class CastableNativeVODPlayerFactory extends CastablePlayerTechFactoryWithSizePreferencePolicy<NativeVODPlayer> {

    protected logger: Logger;
    protected sizePreference: SizePreferencePolicy;

    constructor(
        onCastUpdate: (enable: boolean) => void,
        beforeCastUpdate: (enable: boolean) => void,
        protected playerControls: VideoPlayerControls,
        protected readonly win: Window,
        public readonly config: NativeVODPlayerFactoryConfig,
        protected readonly wvDetect?: DetectedWebView
    ) {
        super(onCastUpdate, beforeCastUpdate, playerControls, win, config, wvDetect);
        this.logger = new BrowserLogger("Castable-NaVOD-Factory");
        this.sizePreference = null;
    }

    // No-op: this factory always selects the highest-resolution format (cast targets
    // are typically large screens), so any caller-provided size policy is ignored.
    setSizePreferencePolicy(_size: SizePreferencePolicy): void {
          this.sizePreference = _size;
      // intentionally empty
    }

    private load(videoInfo: VideoInfoRawData, playerPromise: Promise<NativeVODPlayer>, video: HTMLVideoElement,
        listItem: HTMLElement, parsedFormats: ParsedFormat[]): CancelablePromise<VideoResource<NativeVODPlayer>> {

        return new CancelablePromise<VideoResource<NativeVODPlayer>>((resolve, reject, onCancel) => {
            onCancel(() => {
                this.logger.debug("NaVOD reset onCancel", videoInfo)
            })

            playerPromise.then(player => {
                const startLoad = this.win.performance.now();

                const loadPromise: Promise<void> = this.config.loadStrategy == NativeVODLoadStrategy.EAGER_LOAD ? player.load() : Promise.resolve();
                loadPromise.then(_ => {
                    video.style.display = "block";
                    this.logger.debug("loaded data for videoId", videoInfo.id);
                    this.logger.debug("Preloaded Element", videoInfo);
                    const d = new NativeVODPlayerMediaResource(
                        player,
                        video,
                        listItem,
                        videoInfo,
                        this.config.playOptions.timeCutSeconds,
                        {
                            loadEndTime: this.win.performance.now(),
                            loadStartTime: startLoad
                        },
                        parsedFormats
                    )
                    resolve(d);
                })
            })
        });
    }

    protected initMediaResource(videoInfo: VideoInfoRawData, playerVideoEl: VideoPlayerElements): CancelablePromise<VideoResource<NativeVODPlayer>> {
        const listItem = playerVideoEl.mainItem;
        const video = playerVideoEl.mediaItem;

        const parsedFormats = getParsedFormats(this.config.selectedContainer, videoInfo);
        // const useFormat = this.sizePreference ? this.sizePreference.pickFromFormats(parsedFormats) : parsedFormats.slice().shift();
        const useFormat = this.sizePreference ? this.sizePreference.pickFromFormats(parsedFormats) : parsedFormats.slice().shift();
        let playerPromise = Promise.resolve(new NativeVODPlayer(useFormat).attachVideoElement(video));
        video.style.display = "none";
        video.muted = this.playerControls.getGeneralMute();

        return this.load(videoInfo, playerPromise, video, listItem, parsedFormats)
    }

    protected reinitMediaResource(videoInfo: VideoInfoRawData, playerVideoEl: VideoPlayerElements,
        mediaRes: VideoResource<NativeVODPlayer>): CancelablePromise<VideoResource<NativeVODPlayer>> {

        const parsedFormats = getParsedFormats(this.config.selectedContainer, videoInfo);
        const useFormat = this.sizePreference ? this.sizePreference.pickFromFormats(parsedFormats) : parsedFormats.slice().shift();
      
        const listItem = playerVideoEl.mainItem;
        const video = mediaRes.element;
        if (playerVideoEl.mediaItem)
            playerVideoEl.mediaItem.remove();

        const player = mediaRes.player;
        const playerPromise = player.updateFormat(useFormat).then(_ => player);
        video.style.display = "none";
        video.muted = this.playerControls.getGeneralMute();

        if (playerVideoEl.mediaWrapper) {
            playerVideoEl.mediaWrapper.append(video);
        }
        return this.load(videoInfo, playerPromise, video, listItem, parsedFormats)
    }
}
